import { initialize } from '@telnyx/video';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  EyeShowIcon,
  EyeHideIcon,
  FullscreenIcon,
  MicrophoneMutedIcon,
  MicrophoneUnmutedIcon,
  PhoneIcon,
  PaperPlaneIcon,
  CopyIcon,
} from '@rsos/assets/icons';
import { Box, Button, Loader, Text } from '@rsos/base-components';
import { ButtonBase } from '@rsos/base-components/Button/Button.styles';
import AudioTrack from '@rsos/components/capstone/VideoTo911/AudioTrack';
import MemoizedVideo from '@rsos/components/capstone/VideoTo911/Video';
import {
  CITIZEN,
  CONNECTED,
  DISCONNECTED,
  CONSTRAINTS_DEFAULT,
  STATE_CHANGED,
  STREAM_PUBLISHED,
  SUBSCRIPTION_STARTED,
  ECC_USER,
} from '@rsos/constants/liveVideo/constants';
import useToggleFullscreen from '@rsos/hooks/useToggleFullscreen';
import useSelectedVideoRoom from '@rsos/hooks/videoTo911/useSelectedVideoRoom';
import { useEndRoomMutation } from '@rsos/reduxDataStore/src/features/videoTo911/videoTo911Api';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import isVideoTo911ShareTelnyxEnabled from '@rsos/utils/isVideoTo911ShareTelnyxEnabled';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { notifySuccess } from '@rsos/utils/toastUtils';
import filterStreams from '@rsos/utils/videoTo911/filterStreams';
import {
  trackEndVT911Click,
  trackEndVT911Response,
  trackEndVT911FromServer,
  trackVideoBlurUnblurClick,
  trackVideoToggleFullscreenClick,
  trackVideoMuteUnmuteClick,
} from '../../videoTo911Trackings';
import ShareLiveStreamModal from '../ShareLiveStreamModal';
import {
  BlurContainer,
  BlurOverflowContainer,
  IconWrapper,
} from './LiveVideoCardLegacy.styles';

const useTelnyxUser = () => {
  const profile = useSelector(state => state.sinatra.user.profile);
  const loggedInUser = useMemo(() => {
    const { id = '', first_name = '', last_name = '' } = profile;

    return {
      id: id ? parseInt(id) : '',
      name: `${first_name} ${last_name}`.trim(),
    };
  }, [profile]);
  return loggedInUser;
};

const LiveVideoCardLegacy = () => {
  const roomRef = useRef();
  const addedRemoteStreamRef = useRef(false);
  const citizenVideoRef = useRef();
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true);
  const [isWaitingForCitizen, setisWaitingForCitizen] = useState(true);
  const [isRoomDisconnected, setIsRoomDisconnected] = useState(false);
  const [ownStream, setOwnStream] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const selectedCallerId = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const accountId = useSelector(state => state.psaps?.currentPSAP?.account_id);
  const selectedVideoRoom = useSelectedVideoRoom(selectedCallerId);

  const psaps = useSelector(state => state.psaps);
  const videoShareEnabled = isVideoTo911ShareTelnyxEnabled(psaps);

  const telnyxUser = useTelnyxUser();

  const [fullscreenRef, toggleFullscreen, isFullscreen] = useToggleFullscreen();

  const [triggerEndRoom] = useEndRoomMutation();

  const shareUrl = useMemo(() => {
    if (!selectedVideoRoom?.room_id || !selectedVideoRoom?.refresh_token) {
      return null;
    }
    return `${window.location.protocol}//${window.location.host}/live-video/watch/${selectedVideoRoom.room_id}?refresh_token=${selectedVideoRoom.refresh_token}`;
  }, [selectedVideoRoom]);

  const handleCopyLink = useCallback(() => {
    if (shareUrl) {
      copyToClipboard(shareUrl);
      notifySuccess('Link copied to clipboard');
    }
  }, [shareUrl]);

  const handleBlur = useCallback(() => {
    setIsBlurred(prev => {
      const updateState = !prev;
      trackVideoBlurUnblurClick({
        blurVideo: updateState,
        roomId: selectedVideoRoom?.room_id,
      });
      return updateState;
    });
  }, [selectedVideoRoom]);

  const handleToggleFullscreen = useCallback(() => {
    trackVideoToggleFullscreenClick({
      triggerFullscreen: !isFullscreen,
      roomId: selectedVideoRoom?.room_id,
    });
    toggleFullscreen();
  }, [toggleFullscreen, isFullscreen, selectedVideoRoom]);

  const handleEndStream = useCallback(async () => {
    // Track that the user clicked to end the VT911 session
    trackEndVT911Click({
      roomId: selectedVideoRoom?.room_id,
    });

    triggerEndRoom({
      accountId,
      callerId: selectedCallerId,
      callStartTime: selectedVideoRoom?.callStartTime,
      roomId: selectedVideoRoom?.room_id,
    }).then(async response => {
      await roomRef.current?.disconnect();
      roomRef.current = null;
      citizenVideoRef.current = null;

      // Track the server's response to ending the VT911 session
      trackEndVT911Response({
        responseSuccess: !response?.error,
        roomId: selectedVideoRoom?.room_id,
      });

      if (response.error) {
        captureExceptionWithScope(response.error, {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        });
      }
    });
  }, [accountId, selectedCallerId, selectedVideoRoom, triggerEndRoom]);

  const handleToggleMuteUnmute = useCallback(async () => {
    try {
      const room = roomRef.current;
      const roomState = room.getState();
      const localParticipantId = roomState.localParticipantId;
      const ownStream = await room.getParticipantStream(
        localParticipantId,
        ECC_USER,
      );
      const navigatorStream = await navigator?.mediaDevices?.getUserMedia({
        ...CONSTRAINTS_DEFAULT,
        video: false,
      });
      const audioStream = navigatorStream.getAudioTracks()[0];

      room.updateStream(ECC_USER, {
        audio: ownStream.isAudioEnabled ? null : audioStream,
        video: null,
      });

      setIsAudioMuted(prev => {
        const updateState = !prev;
        trackVideoMuteUnmuteClick({
          muteVideo: updateState,
          roomId: selectedVideoRoom?.room_id,
        });
        return updateState;
      });
    } catch (error) {
      captureExceptionWithScope(error, {
        roomid: selectedVideoRoom?.room_id,
        clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
        refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
      });
    }
  }, [selectedVideoRoom]);

  /**
   * Handle adding user's own audio stream to the room. Invoked when room is in
   * a CONNECTED state
   */
  const handleOwnStream = useCallback(
    // eslint-disable-next-line no-unused-vars
    async state => {
      try {
        const room = roomRef.current;
        // Handle audio stream from user
        const stream = await navigator?.mediaDevices?.getUserMedia({
          ...CONSTRAINTS_DEFAULT,
          video: false,
        });

        const audioStream = stream.getAudioTracks()[0];

        await room.addStream(ECC_USER, {
          audio: audioStream,
          video: null,
        });

        setOwnStream(stream);

        // When starting the current user's stream (audio only), mute self.
        handleToggleMuteUnmute();
      } catch (error) {
        captureExceptionWithScope(error, {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        });
      }
    },
    [selectedVideoRoom, handleToggleMuteUnmute],
  );

  /**
   * Handles adding a remote stream from an external user. Only a max of 2
   * participants are allowed in a room. The current user and the external user.
   * Currently only one video is supported
   * @param {Instance} state - The telnyx video stream state
   * @param {Object} participant - the participant of the stream
   */
  const handleRemoteStream = useCallback(
    async ({ state, participant }) => {
      const room = roomRef.current;
      try {
        if (room) {
          const participantId = participant.id;
          const localParticipantId = state.localParticipantId;
          if (
            participantId &&
            localParticipantId &&
            participantId !== localParticipantId &&
            !addedRemoteStreamRef.current
          ) {
            const participantStream = room.getParticipantStream(
              participantId,
              CITIZEN,
            );
            // External user hasn't fully connected yet
            if (!participantStream) {
              return;
            }

            addedRemoteStreamRef.current = true;

            await room.addSubscription(
              participantId,
              CITIZEN,
              CONSTRAINTS_DEFAULT,
            );

            // Note: participantStream and remoteStream appear to be the same.
            // They are not the same due to the subscription being added
            const remoteStream = room.getParticipantStream(
              participantId,
              CITIZEN,
            );

            const remoteMediaStream = new MediaStream([
              remoteStream.audioTrack,
              remoteStream.videoTrack,
            ]);
            citizenVideoRef.current.srcObject = remoteMediaStream;
          }
        }
      } catch (error) {
        captureExceptionWithScope(error, {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        });
      }
    },
    [selectedVideoRoom],
  );

  const handleRoomDisconnected = useCallback(() => {
    setIsRoomDisconnected(true);
  }, []);

  const telnyxStateCallback = useCallback(
    async state => {
      try {
        if (state.status !== DISCONNECTED) {
          // TODO: timed_out is also possible now
          const streamsCount = state.streams.size;
          const filteredStreams = filterStreams(state.streams, ECC_USER);
          // Assume only one citizen can join
          const foundStream = filteredStreams.find(stream => {
            return stream.key === CITIZEN;
          });

          if (streamsCount > 0 && !!foundStream) {
            setisWaitingForCitizen(false);
          }
        }
      } catch (error) {
        captureExceptionWithScope(error, {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        });
      }
    },
    [selectedVideoRoom],
  );

  const initializeTelnyxVideo = useCallback(async () => {
    const roomId = selectedVideoRoom?.room_id;
    const clientToken = selectedVideoRoom?.client_token;
    const context = JSON.stringify(telnyxUser);

    try {
      const room = await initialize({
        roomId,
        clientToken,
        context,
      });

      roomRef.current = room;

      room.on(STATE_CHANGED, telnyxStateCallback);

      room.on(CONNECTED, async state => {
        const participants = state?.participants;
        const participtantsSize = state?.participants?.size;

        // Handle the video stream from the room if the user refreshes the
        // page or this component is unmounted and remounted
        if (participtantsSize > 1) {
          /**
           * Array.from returns a tuple with the participant ID and participant
           * data. The participant ID is unused. Filter out the current user from
           * the list of participants due to Telnyx keeping track of the current
           * user through page refresh which results in the user being a `remote`
           * user
           */
          const filteredParticipants = Array.from(participants).filter(
            participant => {
              return participant?.[1].context !== JSON.stringify(telnyxUser);
            },
          );
          for (const [, participant] of filteredParticipants) {
            await handleRemoteStream({ state, participant });
          }
        }

        // Handle audio stream from user
        handleOwnStream(state);
      });

      // A stream has been published to the room
      room.on(STREAM_PUBLISHED, async (participantId, streamKey, state) => {
        // Ignore streams from the current user
        const participant = state.participants.get(participantId);
        if (participant.origin !== 'local') {
          // the remote stream is identified using the participantId and streamKey
          // you need to subscribe to a remote stream in order to access it's `MediaStreamTrack`s
          await room.addSubscription(
            participantId,
            streamKey,
            CONSTRAINTS_DEFAULT,
          );
        }
      });

      // A subscription to a remote stream has started
      room.on(SUBSCRIPTION_STARTED, (participantId, streamKey, state) => {
        const participant = state.participants.get(participantId);
        // Ignore streams from the current user
        if (participant.origin !== 'local') {
          const remoteStream = room.getParticipantStream(
            participantId,
            streamKey,
          );

          const remoteMediaStream = new MediaStream([
            remoteStream.audioTrack,
            remoteStream.videoTrack,
          ]);

          // there is a chance that `citizenVideoRef.current` is null if citizen joins first
          if (citizenVideoRef.current) {
            citizenVideoRef.current.srcObject = remoteMediaStream;
          }
        }
      });

      room.on(DISCONNECTED, state => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console -- dev-only: not reached in production
          console.log('disconnected', state);
        }

        trackEndVT911FromServer({
          roomId: selectedVideoRoom?.room_id,
        });

        /**
         * Possible `state` values
         * user_initiated -- User clicks end call. Ends room session
         * unknown -- Citizen refreshed the page or disconnected
         */
        if (state === 'user_initiated') {
          handleRoomDisconnected();
        }
      });

      room.connect().then(() => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console -- dev-only: not reached in production
          console.log('connected');
        }
      });
    } catch (error) {
      captureExceptionWithScope(error, {
        roomid: selectedVideoRoom?.room_id,
        clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
        refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
      });
    }
  }, [
    handleOwnStream,
    handleRemoteStream,
    handleRoomDisconnected,
    selectedVideoRoom,
    telnyxStateCallback,
    telnyxUser,
  ]);

  useEffect(() => {
    // Initialize the room
    if (!roomRef.current) {
      initializeTelnyxVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (roomRef.current && selectedVideoRoom?.client_token) {
      try {
        roomRef.current.updateClientToken(selectedVideoRoom.client_token);
      } catch (error) {
        captureExceptionWithScope(error, {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        });
      }
    }
  }, [selectedVideoRoom]);

  return (
    <Box bg="background" minHeight="300px" py="16px" ref={fullscreenRef}>
      <Box
        margin={isFullscreen ? 'auto' : ''}
        justify="center"
        maxHeight="100vh"
        maxWidth="800px"
        overflow={isFullscreen ? 'auto' : ''}
        width="100%"
      >
        <Box
          horizontal
          justify="space-between"
          paddingLeft="16px"
          paddingRight="40px"
          paddingBottom="8px"
        >
          <Text color="primaryText" fontWeight="bold">
            Video Call
          </Text>
          <Box horizontal gridColumnGap="12px">
            <Box color="primaryText" cursor="pointer" onClick={handleBlur}>
              {isBlurred ? (
                <EyeHideIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                />
              ) : (
                <EyeShowIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                />
              )}
            </Box>
            <Box cursor="pointer" onClick={handleToggleFullscreen}>
              <FullscreenIcon color="primaryText" height="12px" width="12px" />
            </Box>
          </Box>
        </Box>

        {!isRoomDisconnected ? (
          <>
            {isWaitingForCitizen && (
              <Box align="center" justify="center" height="240px">
                <Loader fluid text="Waiting for citizen..." />
              </Box>
            )}
          </>
        ) : (
          <Box align="center" justify="center" height="240px" px="16px">
            <Text color="lightDanger" textAlign="center">
              Stream has been disconnected.
            </Text>
          </Box>
        )}

        <BlurOverflowContainer>
          <BlurContainer
            align="center"
            justify="center"
            display={
              isWaitingForCitizen || isRoomDisconnected ? 'none' : 'flex'
            }
            isBlurred={isBlurred}
          >
            <MemoizedVideo
              ref={citizenVideoRef}
              autoPlay
              onContextMenu={e => {
                e.preventDefault(); // Prevent right clicks
              }}
              style={{ maxHeight: '600px', width: '100%' }}
            />

            <AudioTrack stream={ownStream} autoMute />
          </BlurContainer>
        </BlurOverflowContainer>

        {!isWaitingForCitizen && !isRoomDisconnected && (
          <>
            <Box align="center" horizontal justify="center">
              <ButtonBase btnType="tertiary" onClick={handleToggleMuteUnmute}>
                {!isAudioMuted ? (
                  <MicrophoneUnmutedIcon color="secondaryText" />
                ) : (
                  <MicrophoneMutedIcon color="lightDanger" />
                )}
              </ButtonBase>
            </Box>
          </>
        )}

        <Box align="center" justify="center" gridRowGap="8px">
          <Button
            btnType="warning_tertiary"
            disabled={isRoomDisconnected}
            onClick={handleEndStream}
          >
            <PhoneIcon />
            End Call
          </Button>
          {videoShareEnabled && (
            <Box horizontal columnGap="8px">
              <Button
                btnType="secondary"
                disabled={!shareUrl}
                onClick={() => setIsShareModalOpen(true)}
              >
                <IconWrapper>
                  <PaperPlaneIcon />
                </IconWrapper>
                Share via SMS
              </Button>
              <Button
                btnType="secondary"
                onClick={handleCopyLink}
                disabled={!shareUrl}
              >
                <IconWrapper>
                  <CopyIcon />
                </IconWrapper>
                Copy Link
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <ShareLiveStreamModal
        isOpened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        roomId={selectedVideoRoom?.room_id}
        refreshToken={selectedVideoRoom?.refresh_token}
        shareUrl={shareUrl}
      />
    </Box>
  );
};

export default React.memo(LiveVideoCardLegacy);
